import { useMutation, useQuery } from "react-query";
import { kebabCase } from "lodash";
import useQueryState from "./useQueryState";
import { postFinancialTimeseriesByFeature } from "../services/financialTimeseries";
import { useDebounce } from "./useDebounce";
import { useMemo, useCallback } from "react";
import { useToast } from "../Components/Provider/ToastProvider";
import { useUIStore } from "../store/ui";
import { buildIndicatorName } from "../helpers/indicatorName";

const useTimeseriesFinancial = ({ enabled = true }: { enabled: boolean }) => {
  const { showToast } = useToast();
  const {
    queryState: {
      layers,
      country,
      aggregationLevel,
      geographicalUnit,
      season,
      payout: {
        deductible,
        sumInsured,
        triggerLevel,
        exitLevel,
        maxPayout,
        minPayout,
        geographicalAreas,
        comparisonFocus,
        seasons,
      },
    },
  } = useQueryState();

  const debouncedLayers = useDebounce(
    layers.filter((l) => l.active),
    600,
  );

  const debouncedSeasons = useDebounce(seasons, 600);

  const debouncedPayout = useDebounce(
    { minPayout, maxPayout, deductible, sumInsured, triggerLevel, exitLevel },
    600,
  );

  const isValidHybridLayer = useCallback((layer: any) => {
    if (kebabCase(layer.displayName) !== "hybrid-index") return true;

    const total = (layer.parameters ?? []).reduce(
      (sum: number, p: any) => sum + Number(p.value || 0),
      0,
    );

    return total === 100;
  }, []);

  const isSeasonValid = useMemo(() => {
    if (!debouncedSeasons) return false;

    const s1 = debouncedSeasons["1"] ?? 0;
    const s2 = debouncedSeasons["2"] ?? 0;

    return s1 + s2 === 100;
  }, [debouncedSeasons]);

  const isHybridValid = useMemo(
    () => debouncedLayers.every(isValidHybridLayer),
    [debouncedLayers, isValidHybridLayer],
  );
  const isDetrended = useUIStore((s) => s.isDetrended);
  const isLandCoverMasked = useUIStore((s) => s.isLandCoverMasked);

  const isSystemType =
    aggregationLevel === "0" ||
    aggregationLevel === "1" ||
    aggregationLevel === "2";

  const buildIndicators = useCallback(
    (seasonKey: string) =>
      debouncedLayers
        .filter((layer) => layer.season === seasonKey)
        .map((layer) => {
          const isHybrid = kebabCase(layer.displayName) === "hybrid-index";
          const hybridIndicators =
            layer.parameters?.map((p: any) => ({
              indicator: buildIndicatorName(p.name, {
                isLandCoverMasked,
                isDetrended,
              }),
              weight: Number(p.value),
            })) ?? [];

          return {
            indicatorId: isHybrid
              ? "hybrid"
              : buildIndicatorName(layer.displayName, {
                  isLandCoverMasked,
                  isDetrended,
                }),
            startMonth: Number(layer.months[0]),
            endMonth: Number(layer.months[1]),
            startDekad: Number(layer.dekads[0]),
            endDekad: Number(layer.dekads[1]),
            ...(isHybrid && { hybridIndicators }),
          };
        }),
    [debouncedLayers, isDetrended, isLandCoverMasked],
  );

  const buildPayload = useCallback(
    (download = false) => ({
      download,
      minPayout: debouncedPayout.minPayout,
      maxPayout: debouncedPayout.maxPayout,
      deductible: debouncedPayout.deductible,
      sumInsured: debouncedPayout.sumInsured,
      triggerLevel: debouncedPayout.triggerLevel,
      exitLevel: debouncedPayout.exitLevel,
      country,
      adminLevel: aggregationLevel,
      locationIds:
        comparisonFocus === 0 ? [geographicalUnit] : geographicalAreas,
      comparisonFocus: comparisonFocus === 0 ? 1 : 0,
      seasons: Object.keys(debouncedSeasons).map((s: string) => ({
        season: Number(s),
        percent: debouncedSeasons?.[s],
        indicators: buildIndicators(s),
      })),
      type: isSystemType ? "system" : "user",
    }),
    [
      debouncedPayout,
      debouncedSeasons,
      buildIndicators,
      country,
      aggregationLevel,
      comparisonFocus,
      geographicalUnit,
      geographicalAreas,
      season,
      isSystemType,
    ],
  );

  const queryKey = useMemo(
    () => [
      "timeseries-financial",
      JSON.stringify({
        payout: debouncedPayout,
        layers: debouncedLayers,
        seasons: debouncedSeasons,
        country,
        aggregationLevel,
        comparisonFocus,
        geographicalUnit,
        geographicalAreas,
      }),
      isDetrended,
      isLandCoverMasked,
    ],
    [
      debouncedPayout,
      debouncedLayers,
      debouncedSeasons,
      country,
      aggregationLevel,
      comparisonFocus,
      geographicalUnit,
      geographicalAreas,
      isDetrended,
      isLandCoverMasked,
    ],
  );

  const postFinancialTimeseries = useQuery(
    queryKey,
    () => postFinancialTimeseriesByFeature(buildPayload()),
    {
      enabled:
        enabled &&
        isHybridValid &&
        isSeasonValid &&
        !!country &&
        !!aggregationLevel &&
        debouncedLayers.length > 0 &&
        (comparisonFocus === 0
          ? [geographicalUnit].length > 0
          : geographicalAreas.length > 0),
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const downloadFinancialPayout = useMutation(async () => {
    if (!isHybridValid) {
      showToast("error", "Hybrid Index total distribution should be 100");
      return;
    }

    return postFinancialTimeseriesByFeature(buildPayload(true));
  });

  return {
    postFinancialTimeseries,
    downloadFinancialPayout,
  };
};

export default useTimeseriesFinancial;
